import React, { useMemo, useState } from 'react';
import { ArrowLeft, Brain, Calendar, Clock, TrendingUp, Zap, X } from './Icons';
import { aiLearningAssistantData } from '../data/aiLearningAssistantData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const AILearningAssistantPage = ({ onBack, userPreferences }) => {
  const [currentView, setCurrentView] = useState('main');
  const [selectedFeature, setSelectedFeature] = useState(null);

  const { displayLanguage } = userPreferences;

  const { data: remote } = useSupabaseData({
    table: 'eng_ailearningassistant',
    // Expecting a flexible schema; map common fields and assemble a single object
    mapRow: (row) => row,
    getFallback: () => null
  });

  const data = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) return aiLearningAssistantData;
    const rows = Array.isArray(remote) ? remote : [remote];
    const out = { ...aiLearningAssistantData };
    // Try to group by type field if exists
    rows.forEach((r) => {
      const type = r.type || r.section || null;
      if (type === 'overview') {
        out.overview = {
          id: 'ai_learning_assistant',
          title: { tc: r.title_tc || r.title, sc: r.title_sc || r.title, en: r.title_en || r.title },
          subtitle: { tc: r.subtitle_tc || r.subtitle, sc: r.subtitle_sc || r.subtitle, en: r.subtitle_en || r.subtitle },
          icon: r.icon || 'brain',
          color: r.color || 'blue'
        };
      } else if (type === 'feature') {
        out.features = out.features || [];
        out.features.push({
          id: r.feature_id || r.id,
          title: { tc: r.title_tc || r.title, sc: r.title_sc || r.title, en: r.title_en || r.title },
          description: { tc: r.description_tc || r.description, sc: r.description_sc || r.description, en: r.description_en || r.description },
          icon: r.icon || 'calendar',
          buttonText: { tc: r.button_text_tc || '查看', sc: r.button_text_sc || '查看', en: r.button_text_en || 'View' },
          buttonColor: r.button_color || 'blue',
          action: r.action || 'view_today_plan'
        });
      } else if (type === 'weekly_summary') {
        out.weeklySummary = {
          id: 'weekly_learning_summary',
          title: { tc: r.title_tc || '本週學習摘要', sc: r.title_sc || '本周学习摘要', en: r.title_en || "This Week's Learning Summary" },
          stats: [
            { id: 'study_hours', value: r.study_hours ?? '0', label: { tc: '小時', sc: '小时', en: 'Hours' }, color: 'blue' },
            { id: 'average_score', value: r.average_score ? `${r.average_score}%` : '0%', label: { tc: '平均分數', sc: '平均分数', en: 'Average Score' }, color: 'green' },
            { id: 'completed_courses', value: r.completed_courses ?? '0', label: { tc: '完成課程', sc: '完成课程', en: 'Completed Courses' }, color: 'purple' },
            { id: 'new_vocabulary', value: r.new_vocabulary ? `+${r.new_vocabulary}` : '+0', label: { tc: '新詞彙', sc: '新词汇', en: 'New Vocabulary' }, color: 'orange' }
          ]
        };
      } else if (type === 'reminder') {
        out.reminders = out.reminders || { id: 'learning_reminders', title: { tc: '學習提醒設定', sc: '学习提醒设定', en: 'Learning Reminder Settings' }, options: [] };
        out.reminders.options.push({
          id: r.id,
          time: r.reminder_time || r.time,
          label: { tc: r.label_tc || r.label, sc: r.label_sc || r.label, en: r.label_en || r.label },
          enabled: r.enabled !== false
        });
      } else if (type === 'qa') {
        out.qaSystem = out.qaSystem || { id: 'qa_system', title: { tc: 'AI 即時問答', sc: 'AI 即时问答', en: 'AI Instant Q&A' }, placeholder: { tc: '輸入你的英語問題...', sc: '输入你的英语问题...', en: 'Enter your English question...' }, recentQuestions: [] };
        if (r.question_en || r.question_tc || r.question_sc) {
          out.qaSystem.recentQuestions.push({
            id: r.id,
            question: { tc: r.question_tc || '', sc: r.question_sc || '', en: r.question_en || '' },
            answer: { tc: r.answer_tc || '', sc: r.answer_sc || '', en: r.answer_en || '' },
            timestamp: r.created_at || new Date().toISOString()
          });
        }
      } else if (type === 'today_task') {
        out.todayPlan = out.todayPlan || { id: 'today_learning_plan', title: { tc: '今日學習計劃', sc: '今日学习计划', en: "Today's Learning Plan" }, tasks: [] };
        out.todayPlan.tasks.push({
          id: r.id,
          time: r.time || '00:00',
          title: { tc: r.title_tc || r.title, sc: r.title_sc || r.title, en: r.title_en || r.title },
          duration: r.duration || '',
          status: r.status || 'pending'
        });
      }
    });
    return out;
  }, [remote]);

  const getText = (textObj) => {
    return textObj[displayLanguage] || textObj.en;
  };

  const getButtonColor = (color) => {
    const colors = {
      blue: 'bg-blue-500 hover:bg-blue-600',
      green: 'bg-green-500 hover:bg-green-600',
      purple: 'bg-purple-500 hover:bg-purple-600',
      yellow: 'bg-yellow-500 hover:bg-yellow-600',
      orange: 'bg-orange-500 hover:bg-orange-600'
    };
    return colors[color] || colors.blue;
  };

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
    setCurrentView(feature.action);
  };

  const renderMainView = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="w-6"></div>
        <h1 className="text-xl font-bold">{getText(data.overview.title)}</h1>
        <button 
          onClick={onBack}
          className="flex items-center text-white hover:text-blue-300 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* AI Brain Icon */}
      <div className="flex justify-center mb-8">
        <div className="relative">
          <div className="w-24 h-24 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
            <Brain size={48} className="text-white" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
        </div>
      </div>

      {/* AI Features */}
      <div className="px-4 space-y-4 mb-8">
        {data.features.map((feature) => (
          <div 
            key={feature.id}
            className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 shadow-lg"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                {feature.icon === 'calendar' && <Calendar size={24} className="text-white" />}
                {feature.icon === 'clock' && <Clock size={24} className="text-white" />}
                {feature.icon === 'trending-up' && <TrendingUp size={24} className="text-white" />}
                {feature.icon === 'zap' && <Zap size={24} className="text-white" />}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2 text-white">{getText(feature.title)}</h3>
                <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                  {getText(feature.description)}
                </p>
                <button
                  onClick={() => handleFeatureClick(feature)}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors shadow-md ${getButtonColor(feature.buttonColor)}`}
                >
                  {getText(feature.buttonText)}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Weekly Learning Summary */}
      <div className="px-4 mb-8">
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-white">{getText(data.weeklySummary.title)}</h3>
          <div className="grid grid-cols-2 gap-4">
            {data.weeklySummary.stats.map((stat) => (
              <div key={stat.id} className="text-center">
                <div className={`text-3xl font-bold mb-1 ${
                  stat.color === 'blue' ? 'text-blue-200' :
                  stat.color === 'green' ? 'text-green-200' :
                  stat.color === 'purple' ? 'text-purple-200' :
                  'text-orange-200'
                }`}>
                  {stat.value}
                </div>
                <div className="text-sm text-white/90 font-medium">{getText(stat.label)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );

  const renderTodayPlan = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-purple-900 via-blue-900 to-purple-800 text-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={() => setCurrentView('main')}
            className="flex items-center text-white hover:text-blue-300 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">{getText(data.todayPlan.title)}</h1>
          <div className="w-6"></div>
        </div>

        <div className="px-4 space-y-4 pb-4">
          {data.todayPlan.tasks.map((task) => (
            <div key={task.id} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    task.status === 'completed' ? 'bg-green-500' :
                    task.status === 'in_progress' ? 'bg-yellow-500' :
                    'bg-gray-500'
                  }`}></div>
                  <div>
                    <div className="text-sm text-gray-300">{task.time}</div>
                    <h3 className="font-semibold">{getText(task.title)}</h3>
                  </div>
                </div>
                <div className="text-sm text-gray-300">{task.duration}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderReminders = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-purple-900 via-blue-900 to-purple-800 text-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={() => setCurrentView('main')}
            className="flex items-center text-white hover:text-blue-300 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">{getText(data.reminders.title)}</h1>
          <div className="w-6"></div>
        </div>

        <div className="px-4 space-y-4 pb-4">
          {data.reminders.options.map((reminder) => (
            <div key={reminder.id} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Clock size={20} className="text-blue-400" />
                  <div>
                    <div className="text-sm text-gray-300">{reminder.time}</div>
                    <h3 className="font-semibold">{getText(reminder.label)}</h3>
                  </div>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 ${
                  reminder.enabled ? 'bg-green-500 border-green-500' : 'border-gray-400'
                }`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAnalysis = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-purple-900 via-blue-900 to-purple-800 text-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={() => setCurrentView('main')}
            className="flex items-center text-white hover:text-blue-300 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">{getText(data.progressAnalysis.title)}</h1>
          <div className="w-6"></div>
        </div>

        <div className="px-4 space-y-6 pb-4">
          {/* Weekly Progress Chart */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <h3 className="text-lg font-semibold mb-4">{getText(data.progressAnalysis.charts[0].title)}</h3>
            <div className="h-32 bg-gray-800 rounded-lg flex items-end justify-between p-4 space-x-1">
              {data.progressAnalysis.charts[0].data.map((item, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div 
                    className="bg-blue-500 rounded-t w-6 mb-2"
                    style={{ height: `${item.value}%` }}
                  ></div>
                  <span className="text-xs text-gray-300">{item.day}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Insights */}
          <div className="space-y-4">
            {data.progressAnalysis.insights.map((insight) => (
              <div key={insight.id} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <h3 className={`font-semibold mb-2 ${
                  insight.type === 'strength' ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  {getText(insight.title)}
                </h3>
                <p className="text-gray-300 text-sm">{getText(insight.content)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderQA = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-purple-900 via-blue-900 to-purple-800 text-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={() => setCurrentView('main')}
            className="flex items-center text-white hover:text-blue-300 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">{getText(data.qaSystem.title)}</h1>
          <div className="w-6"></div>
        </div>

        <div className="px-4 space-y-4 pb-4">
          {/* Input Area */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <textarea
              placeholder={getText(data.qaSystem.placeholder)}
              className="w-full bg-transparent border-none outline-none text-white placeholder-gray-400 resize-none"
              rows="3"
            />
            <button className="mt-3 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              {getText({ tc: "發送", sc: "发送", en: "Send" })}
            </button>
          </div>

          {/* Recent Questions */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{getText({ tc: "最近問題", sc: "最近问题", en: "Recent Questions" })}</h3>
            {data.qaSystem.recentQuestions.map((qa) => (
              <div key={qa.id} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="mb-3">
                  <h4 className="font-semibold text-blue-300 mb-1">{getText(qa.question)}</h4>
                  <p className="text-gray-300 text-sm">{getText(qa.answer)}</p>
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(qa.timestamp).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Render based on current view
  switch (currentView) {
    case 'view_today_plan':
      return renderTodayPlan();
    case 'set_reminder':
      return renderReminders();
    case 'view_analysis':
      return renderAnalysis();
    case 'start_asking':
      return renderQA();
    default:
      return renderMainView();
  }
};

export default AILearningAssistantPage;
